import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, InputNumber, Modal, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { REASON_CODES } from '@constants/index';

interface InventoryTypeOption {
  id: string;
  name: string;
  full_name?: string;
  code?: string;
}

interface Inventory {
  id: string;
  product?: { id?: string; sku?: string | null; name?: string | null } | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  available_quantity?: number;
  inventory_type?: { id: string; name?: string } | null;
  inventory_type_id?: string;
  inventory_type_name?: string;
}

interface ItemPayload {
  inventory_id: string;
  quantity: number;
  target_inventory_type_id: string;
  reason_code?: string;
  note?: string;
  // Display-only metadata for the items table on the New page:
  __product_sku?: string;
  __product_name?: string;
  __batch?: string | null;
  __source_inventory_type_name?: string;
  __target_inventory_type_name?: string;
}

interface Props {
  visible: boolean;
  inventory: Inventory | null;
  /** Items already added in the current transformation draft. Used to subtract
   *  quantities already committed against the same inventory so the merchant
   *  can't oversubscribe via repeated picks. */
  existingItems?: ItemPayload[];
  inventoryTypes: InventoryTypeOption[];
  defaultReasonCode?: string;
  onCancel: () => void;
  onConfirm: (payload: ItemPayload) => void;
}

const sourceInventoryTypeId = (inv: Inventory | null) =>
  inv?.inventory_type?.id ?? inv?.inventory_type_id ?? null;

const sourceInventoryTypeName = (inv: Inventory | null) =>
  inv?.inventory_type?.name ?? inv?.inventory_type_name ?? null;

export default function AdminTransformationItemModal({
  visible,
  inventory,
  existingItems = [],
  inventoryTypes,
  defaultReasonCode,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);

  const sourceTypeId = sourceInventoryTypeId(inventory);
  const sourceTypeName = sourceInventoryTypeName(inventory);

  // Subtract quantities already drafted against this same inventory so the
  // remaining cap reflects what the merchant actually has left to allocate.
  const alreadyDraftedQty = useMemo(
    () => existingItems
      .filter(i => i.inventory_id === inventory?.id)
      .reduce((sum, i) => sum + (Number(i.quantity) || 0), 0),
    [existingItems, inventory]
  );

  const remainingAvailable = Math.max(
    0,
    (inventory?.available_quantity ?? 0) - alreadyDraftedQty
  );

  // Source type is excluded from selectable targets — converting to the same
  // type is a no-op and the backend will reject it.
  const targetTypeOptions = useMemo(
    () => inventoryTypes
      .filter(it => it.id !== sourceTypeId)
      .map(it => ({ value: it.id, label: it.full_name || it.name })),
    [inventoryTypes, sourceTypeId]
  );

  useEffect(() => {
    if (visible && inventory) {
      form.resetFields();
      setError(null);
      form.setFieldsValue({
        quantity: undefined,
        target_inventory_type_id: undefined,
        reason_code: defaultReasonCode,
        note: undefined,
      });
    }
  }, [visible, inventory, defaultReasonCode, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!inventory) return;

      const targetType = inventoryTypes.find(it => it.id === values.target_inventory_type_id);

      onConfirm({
        inventory_id: inventory.id,
        quantity: Number(values.quantity),
        target_inventory_type_id: values.target_inventory_type_id,
        reason_code: values.reason_code,
        note: values.note,
        __product_sku: inventory.product?.sku ?? undefined,
        __product_name: inventory.product?.name ?? undefined,
        __batch: inventory.batch,
        __source_inventory_type_name: sourceTypeName ?? undefined,
        __target_inventory_type_name: targetType?.name,
      });
    } catch (e) {
      // Form validation surfaces its own messages — nothing more to do.
    }
  };

  return (
    <Modal
      title={t('shared.inventory_transformation.add_item', '新增轉換明細')}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t('common.button.confirm')}
      cancelText={t('common.button.cancel')}
      destroyOnClose
    >
      {inventory && (
        <>
          <div style={{ marginBottom: 16 }}>
            <div><strong>{inventory.product?.sku}</strong> - {inventory.product?.name}</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              {t('models.inventory.batch')}: {inventory.batch || '-'}
              {' / '}
              {t('models.inventory.expiration_date')}: {inventory.expiration_date || '-'}
            </div>
            <div style={{ marginTop: 8 }}>
              <Tag>{t('models.inventory.source_type', '來源類型')}: {sourceTypeName ?? '-'}</Tag>
              <Tag color="success">
                {t('models.inventory.available_quantity', '可用')}: {remainingAvailable}
              </Tag>
            </div>
          </div>

          {targetTypeOptions.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('shared.inventory_transformation.no_other_types', '此貨主只有一個庫存類型，無法轉換')}
              style={{ marginBottom: 16 }}
            />
          )}

          {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

          <Form form={form} layout="vertical">
            <Form.Item
              label={t('models.inventory_transformation_item.quantity')}
              name="quantity"
              rules={[
                { required: true, message: t('common.form.required') },
                {
                  validator: (_, value) => {
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) {
                      return Promise.reject(new Error(t('shared.inventory_transformation.error.quantity_positive', '數量必須大於 0')));
                    }
                    if (n > remainingAvailable) {
                      return Promise.reject(new Error(
                        t('shared.inventory_transformation.error.quantity_exceeds_available', {
                          defaultValue: '數量不可超過可用 {{max}}',
                          max: remainingAvailable,
                        })
                      ));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber min={1} max={remainingAvailable} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('models.inventory_transformation_item.target_inventory_type')}
              name="target_inventory_type_id"
              rules={[{ required: true, message: t('common.form.required') }]}
            >
              <Select
                options={targetTypeOptions}
                placeholder={t('shared.inventory_transformation.target_type_placeholder', '選擇目標庫存類型')}
                disabled={targetTypeOptions.length === 0}
              />
            </Form.Item>

            <Form.Item
              label={t('models.inventory_transformation_item.reason_code')}
              name="reason_code"
            >
              <Select
                allowClear
                options={REASON_CODES.map((code: string) => ({
                  value: code,
                  label: t(`enums.stock_adjustment.reason_code.${code}`, code),
                }))}
                placeholder={t('shared.inventory_transformation.reason_placeholder', '選擇原因（可選）')}
              />
            </Form.Item>

            <Form.Item
              label={t('models.inventory_transformation_item.note')}
              name="note"
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
